import React, { useEffect, useState } from "react";
import { Alert, Button, Checkbox, Form, Input, Select, Spin } from "antd";
import { makeApiRequest } from "../../lib/api";
import { TypeParams } from "../../types";
import { useTranslation } from "react-i18next"; // Add this import

const { Option } = Select;

interface ProductOrderProps {
  params: TypeParams;
}

const ProductOrder: React.FC<ProductOrderProps> = ({ params }) => {
  const { t } = useTranslation(); // Add this line
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [lastOrderedProducts, setLastOrderedProducts] = useState<Record<string, any>>([]);
  const [customerAddress, setCustomerAddress] = useState("");
  const [amountOptions, setAmountOptions] = useState<number[]>([]);
  const [productCategoryToOrder] = useState<any>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const HEADER = <></>;

  const handleCheckboxChange = (e: any, category: string) => {
    productCategoryToOrder[category] = e.target.checked;
    const atleastOne = Object.values(productCategoryToOrder).filter((x) => !!x).length > 0;
    setShowAddressForm(atleastOne);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch last ordered products and customer address
        const productsResponse = await makeApiRequest(params, "REACT_GET_LAST_ORDERED_PRODUCTS", {});
        const parsedProducts = JSON.parse(productsResponse);
        if (!parsedProducts || parsedProducts.STATUS !== "OK") {
          setError(t("bestellung.error"));
          setLoading(false);
          return;
        }
        setLastOrderedProducts(parsedProducts.CONTENT);
        const addressResponse = await makeApiRequest(params, "REACT_GET_CUSTOMER_ADDRESS", {});
        const parsedAddress = JSON.parse(addressResponse);
        if (!parsedAddress || parsedAddress.STATUS !== "OK") {
          setError(t("bestellung.error"));
          setLoading(false);
          return;
        }
        setCustomerAddress(parsedAddress.CONTENT);

        // Prepare amount options
        const AMOUNT_MAX = 3;
        const options = Array.from({ length: AMOUNT_MAX }, (_, i) => i + 1);
        setAmountOptions(options);

        setLoading(false);
      } catch (error) {
        setError(t("bestellung.error"));
        setLoading(false);
      }
    };

    fetchData();
  }, [params, t]);

  const onFinish = async (values: any) => {
    const products: any[] = [];
    Object.entries(productCategoryToOrder).forEach(([cat, bought]) => {
      if (!bought) {
        return;
      }
      products.push({
        category: cat,
        product: values[`ordered_product_${cat}`],
        amount: values[`amount_${cat}`] ? values[`amount_${cat}`] : 1,
        transaction_id: values[`last_transaction_id_${cat}`],
        agreed: bought,
      });
    });

    // console.log(products);
    setIsSubmitting(true); // Set loading state

    try {
      const payload = {
        ordered_products: products,
        paper_bill_preferred: values.paper_bill_preferred,
        comment: values.comment,
      };
      const response = await makeApiRequest(params, "REACT_PRODUCT_ORDER_SUBMIT", payload);
      const parsedResponse = JSON.parse(response);

      if (parsedResponse && parsedResponse.STATUS === "OK") {
        setSuccess(true);
      } else {
        setSuccess(false);
        setError(t("bestellung.error"));
      }
    } catch (error) {
      setSuccess(false);
      setError(t("bestellung.error"));
    } finally {
      setIsSubmitting(false); // Reset loading state
    }
  };

  if (loading) {
    return (
      <div className="my-lg">
        <Spin />
      </div>
    );
  }

  // SUCCESS HANDLING
  if (success) {
    return (
      <div>
        {HEADER}

        <p>{t("bestellung.success")}</p>
      </div>
    );
  }

  //Error handling
  if (error) {
    return (
      <div>
        {/* <h1>Produktbestellung</h1> */}
        {HEADER}

        {error && (
          <>
            <Alert message={error} type="error" />
            <br />
            <br />
          </>
        )}
      </div>
    );
  }

  return (
    <div>
      {/* <h1>Produktbestellung</h1> */}
      {HEADER}
      <Form form={form} onFinish={onFinish}>
        {lastOrderedProducts && Object.keys(lastOrderedProducts).length > 0 ? (
          <>
            <Form.Item>
              <p>{t("bestellung.replace_notice")}</p>
            </Form.Item>
            {Object.entries(lastOrderedProducts).map(([cat, product]) => (
              <div key={cat}>
                <Form.Item>
                  <strong>{product.last_product}</strong>
                </Form.Item>
                <Form.Item>
                  <p>{t("bestellung.last_order", { amount: product.last_amount, date: product.date_last_order })}</p>
                </Form.Item>

                {/* agreed */}
                <Form.Item name={`product_agreed_${cat}`} valuePropName="checked">
                  <Checkbox onChange={(e) => handleCheckboxChange(e, cat)}>
                    {t("bestellung.reorder", { price: product.current_price })}
                  </Checkbox>
                </Form.Item>
                {productCategoryToOrder[cat] && (
                  <Form.Item name={`amount_${cat}`} label={t("bestellung.amount")}>
                    <Select defaultValue={1} style={{ width: 70 }}>
                      {amountOptions.map((option) => (
                        <Option key={option} value={option}>
                          {option}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                )}
                <Form.Item name={`ordered_product_${cat}`} initialValue={product.last_product} hidden>
                  <Input />
                </Form.Item>
                <Form.Item name={`last_transaction_id_${cat}`} initialValue={product.last_transaction_id} hidden>
                  <Input />
                </Form.Item>
              </div>
            ))}
            <Form.Item>
              <p>{t("bestellung.shipping_cost")}</p>
            </Form.Item>

            {showAddressForm && (
              <>
                <Form.Item>
                  <h4>{t("bestellung.address_comments")}</h4>
                  <p>
                    {t("bestellung.shipping_address")}
                    <br />
                    &nbsp;
                  </p>
                  <pre>
                    {customerAddress.split("\\n").map((line, index) => (
                      <React.Fragment key={index}>
                        {line}
                        <br />
                      </React.Fragment>
                    ))}
                  </pre>
                  <Form.Item name="paper_bill_preferred" valuePropName="checked">
                    <Checkbox>{t("bestellung.paper_bill")}</Checkbox>
                  </Form.Item>
                  <Form.Item name="comment" label={t("bestellung.comment")}>
                    <Input.TextArea rows={4} maxLength={512} />
                  </Form.Item>
                </Form.Item>
                <Form.Item>
                  <Button type="primary" htmlType="submit" loading={isSubmitting}>
                    {t("bestellung.submit")}
                  </Button>
                </Form.Item>
              </>
            )}
          </>
        ) : (
          <div>
            <h2>{t("bestellung.no_product_registration")}</h2>
            <p>{t("bestellung.no_product_message")}</p>
            <Button type="primary" onClick={() => (window.location.href = `/${params.language}/go/aftercare/places`)}>
              {t("bestellung.aftercare_button")}
            </Button>
          </div>
        )}
      </Form>
    </div>
  );
};

export default ProductOrder;
